/**
 * Created by Ning Jiang on 2021-05-07.
 */

import {DeadwoodRipLayers, Layers} from "../view/DeadwoodRipLayers";
import {DeadwoodRipSymbol} from "../symbol/DeadwoodRipSymbol";
import {DeadwoodRipSymbolConfig} from "../symbol/DeadwoodRipSymbolConfig";
import {ReelState} from "../../core/reel/ReelState";
import {gsap} from "gsap";
import {DeadwoodRip} from "../DeadwoodRip";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";

export class DeadwoodRipReelView {

    // TODO: Change the width wider to make the mask wider for wide symbol animations.
    // The x position is for the center middle x position of a reel view (center middle x position of symbol).
    // The y position is the top of the reel view. the top position of the top symbol in a reel.
    public static REEL_AREAS:PIXI.Rectangle[] = [
        new PIXI.Rectangle(72, 72 + DeadwoodRipSymbol.DEFAULT_HEIGHT * 0.5, DeadwoodRipSymbol.DEFAULT_WIDTH * 2, DeadwoodRipSymbol.DEFAULT_HEIGHT * 3),
        new PIXI.Rectangle(216, 72, DeadwoodRipSymbol.DEFAULT_WIDTH * 3, DeadwoodRipSymbol.DEFAULT_HEIGHT * 4),
        new PIXI.Rectangle(360, 72, DeadwoodRipSymbol.DEFAULT_WIDTH * 3, DeadwoodRipSymbol.DEFAULT_HEIGHT * 4),
        new PIXI.Rectangle(504, 72, DeadwoodRipSymbol.DEFAULT_WIDTH * 3, DeadwoodRipSymbol.DEFAULT_HEIGHT * 4),
        new PIXI.Rectangle(648, 72 + DeadwoodRipSymbol.DEFAULT_HEIGHT, DeadwoodRipSymbol.DEFAULT_WIDTH * 5, DeadwoodRipSymbol.DEFAULT_HEIGHT * 2)
    ]

    public static getGlobalPosition(reelId:number, symbolId:number):PIXI.Point {
        return new PIXI.Point(
            DeadwoodRipReelView.REEL_AREAS[reelId].x,
            DeadwoodRipReelView.REEL_AREAS[reelId].y + DeadwoodRipSymbol.DEFAULT_HEIGHT * (symbolId + 0.5)
        );
    }

    public reelId:number;
    public allSymbols:DeadwoodRipSymbol[] = [];

    private readonly _lowContainer:PIXI.Container;
    private readonly _highContainer:PIXI.Container;
    private readonly _lowWinContainer:PIXI.Container;
    private readonly _highWinContainer:PIXI.Container;
    // TODO: have more layers for sticky symbols and so on.

    public wantBounce:boolean = false;
    public spinStepOffset:number = 0;
    private _lastStepOffset:number;
    public nextSpinStepConfig:{
        reelState:ReelState,
        duration?:number,
        ease?:string
    } | null;
    private _symbolsNumTop:number;
    private _stepDuration:number;
    private _stepEase:string = "none";
    private _currentTl:TimelineLite;
    private _positionsSnapshot:number[];
    private _isPositionSnapshotDirty:boolean = true;

    public get stepDistance():number {
        return this.allSymbols[this._symbolsNumTop].symbolStep;
    }

    public get currentStepLeftTime():number {
        if(this._currentTl != null && this._currentTl.isActive()) {
            return this._currentTl.duration() * (1 - this._currentTl.progress());
        }
        return 0;
    }

    public get currentStepDuration():number {
        if(this._currentTl) {
            return this._currentTl.duration();
        }
        return 0;
    }

    constructor(reelId:number) {
        this.reelId = reelId;

        this._lowContainer = this.createSymbolContainer(reelId);
        DeadwoodRipLayers.getLayer(Layers.LOW_SYMBOLS)?.addChild(this._lowContainer);
        this._highContainer = this.createSymbolContainer(reelId);
        DeadwoodRipLayers.getLayer(Layers.HIGH_SYMBOLS)?.addChild(this._highContainer);
        this._lowWinContainer = this.createSymbolContainer(reelId);
        DeadwoodRipLayers.getLayer(Layers.LOW_SYMBOLS_WIN)?.addChild(this._lowWinContainer);
        this._highWinContainer = this.createSymbolContainer(reelId);
        DeadwoodRipLayers.getLayer(Layers.HIGH_SYMBOLS_WIN)?.addChild(this._highWinContainer);
    }

    private createSymbolContainer(reelId:number):PIXI.Container {
        const mask:PIXI.Graphics = new PIXI.Graphics();
        mask.beginFill(0xFF0000, 1);
        mask.drawShape(new PIXI.Rectangle(-DeadwoodRipReelView.REEL_AREAS[reelId].width * 0.5, 0, DeadwoodRipReelView.REEL_AREAS[reelId].width, DeadwoodRipReelView.REEL_AREAS[reelId].height));
        mask.endFill();

        const container = new PIXI.Container();
        container.position.set(DeadwoodRipReelView.REEL_AREAS[reelId].x, DeadwoodRipReelView.REEL_AREAS[reelId].y);
        container.addChild(mask);
        container.mask = mask;
        return container;
    }

    public addSymbol(symbol:DeadwoodRipSymbol):void {
        if(DeadwoodRipSymbolConfig.SYMBOLS[symbol.stackedSymbolName?.symName].highSymbol) {
            this._highContainer.addChild(symbol);
        } else {
            this._lowContainer.addChild(symbol);
        }
    }

    public addWinSymbol(symbol:DeadwoodRipSymbol):void {
        if(DeadwoodRipSymbolConfig.SYMBOLS[symbol.stackedSymbolName?.symName].highSymbol) {
            this._highWinContainer.addChild(symbol);
        } else {
            this._lowWinContainer.addChild(symbol);
        }
    }

    public removeSymbol(symbol:DeadwoodRipSymbol):void {
        if(symbol.parent) {
            symbol.parent.removeChild(symbol);
        }
    }

    public initSymbolsPosition(symbolsNumTop:number):void {
        this._symbolsNumTop = symbolsNumTop;
        let offset:number = 0;
        for(let i = symbolsNumTop; i < this.allSymbols.length; i++) {
            this.allSymbols[i].y = offset + this.allSymbols[i].symbolHeight * 0.5;
            offset += this.allSymbols[i].symbolStep;
        }
        if(symbolsNumTop > 0) {
            offset = 0;
            for(let i = symbolsNumTop - 1; i >= 0; i--) {
                this.allSymbols[i].y = offset - this.allSymbols[i].bottomPadding - this.allSymbols[i].symbolHeight * 0.5;
                offset -= this.allSymbols[i].symbolStep;
            }
        }
    }

    private updatePositionSnapshot():void {
        // To snapshot the symbols start posion for each step. it's not sure if the onUpdate or onStart triggered first, so need to trigger this one in both places.
        if(this._isPositionSnapshotDirty) {
            this._positionsSnapshot = ArrayHelper.initArrayWithValues(this.allSymbols.length, index => this.allSymbols[index].y);
            this._isPositionSnapshotDirty = false;
            this._lastStepOffset = 0;
        }
    }

    public step(newSymbol:DeadwoodRipSymbol, removeSymbolCallback:() => void, stepDistance?:number,):TimelineLite {
        const reelState:ReelState | undefined = this.nextSpinStepConfig?.reelState;
        const stepDuration:number = this.nextSpinStepConfig?.duration ? this.nextSpinStepConfig.duration : this._stepDuration;
        const stepEase:string = this.nextSpinStepConfig?.ease ? this.nextSpinStepConfig.ease : this._stepEase;
        if(stepDistance == null) {
            stepDistance = this.stepDistance; // stepDistance is the size of first coming symbol.
        }
        this.nextSpinStepConfig = null;

        this._currentTl = gsap.timeline();
        this._currentTl.add(() => {
            if(reelState) {
                DeadwoodRip.reels[this.reelId].reelState = reelState;
            }
            this._stepDuration = stepDuration;
            this._stepEase = stepEase;
            this.spinStepOffset = 0;
            this.updatePositionSnapshot();
        });
        this._currentTl.add(gsap.to(this,
            {
                duration : stepDuration,
                ease : stepEase,
                spinStepOffset : stepDistance,
                onUpdate : () => {
                    this.allSymbols.forEach((symbol, index) => {
                        symbol.y = this._positionsSnapshot[index] + this.spinStepOffset;
                    });
                    if(this.wantBounce && this.spinStepOffset < this._lastStepOffset) {
                        this.doBounce();
                        return;
                    }
                    this._lastStepOffset = this.spinStepOffset;
                }
            }));
        this._currentTl.add(() => {
            this._isPositionSnapshotDirty = true;
            removeSymbolCallback();
            if(this.wantBounce) {
                this.doBounce();
            }
        })

        return this._currentTl;
    }

    private doBounce():void {
        this.wantBounce = false;
        DeadwoodRip.reels[this.reelId].reelState = ReelState.BOUNCE;
    }
}